/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// External/Third-party dependencies
import { useContext, useEffect, useMemo, useState } from 'react';

// Internal dependencies
import { ThemeContext } from '@rakuten/contexts/ThemeContext';
import { StyleSheet } from "@rakuten/modules-core-components";
import useLocateService from '@rakuten/hooks/useLocateService';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

// Tag for log output etc.
const TAG = '[hook/useStyleSystem]';


// -----------------------------------------------------------------------------
// HELPER
// -----------------------------------------------------------------------------

/**
 * Wrapper function to create an RN StyleSheet with styles for a component who
 * is identified via the provided {@param id}. For the web, the generated
 * class names are prefixed with {@param id}.
 *
 * @param {function} styleFn Function to create style objects.
 * @param {string} id An ID to identify a component
 * @param {*} args Arguments that will be passed to the {@param styleFn}
 * @return {Object} Object mapping style names to style objects _or_ class names (web)
 */
function createStyleSheet(styleFn, id, args) {
  return StyleSheet.create(styleFn(args), id);
}


// -----------------------------------------------------------------------------
// STYLE SYSTEM HOOK
// -----------------------------------------------------------------------------

/**
 * A hook to calculate styles
 *
 * @param {object} options Hook options
 * @param {object} options.props Current props of the component that uses the hook
 * @param {object} options.styleFn Function that will be called to calculate styles
 * @param {string} [options.id] ID of the component; used for class names
 * @param {bool} [shouldWatchBreakpoint=false] Should styles be recalculated if
 *               the currently matching breakpoint changes
  * @param {object} [watchedProps=[ ]] List of variables/references; if any of
 *                  these change, styles will be recalculated
 * @returns {object} Calculated styles
 */
export default function useStyleSystem(options = {}, watchedProps = []) {
  const logger = useLocateService(["logger"]);

  const props = options.props;
  let styleFn = options.styleFn;
  let id = options.id;
  const shouldWatchBreakpoint = options.shouldWatchBreakpoint || false;
  const shouldWatchOrientation = options.shouldWatchOrientation || false;

  const context = useContext(ThemeContext);
  const theme = context.theme;
  const breakpoint = context.breakpoint;
  const orientation = context.orientation;

  // If no component ID has been provided, we need to generate a random
  // one to prevent the same styles being applied to two different components
  if (!id || id.length === 0) {
    id = `Component_${Math.random().toString(16).slice(-8)}`;
    logger?.warn(`${TAG} No ID provided, using auto-generated id "${id}" to prevent style name duplication`);
  }

  // If no style function has been provided, we cannot create styles for this
  // component. To prevent app crashes, we use a dummy function.
  if (!styleFn) {
    logger?.warn(`${TAG} No style function for "${id}" provided; styling not possible`);
    styleFn = () => { };
  }

  // Apply standard props that will be watched to determine when to re-calculate
  // styles.
  if(theme){
    watchedProps.push(theme.name);
  }
  if (shouldWatchBreakpoint === true) {
    watchedProps.push(breakpoint);
  }
  if (shouldWatchOrientation === true) {
    watchedProps.push(orientation);
  }

  // Determine args to pass to style sheet creator
  const args = { props, theme };
  if (shouldWatchBreakpoint === true) {
    args.breakpoint = breakpoint;
  }
  if (shouldWatchOrientation === true) {
    args.orientation = orientation;
  }

  const calculatedStyles = useMemo(
    () => createStyleSheet(styleFn, id, args),
    watchedProps
  );

  const [styles, setStyles] = useState(calculatedStyles);

  // Apply styles to internal hook state
  const updateStyles = () => setStyles(calculatedStyles);

  // Whenever any of the watched properties changes - if there are any -, we
  // need to recalculate the styles
  useEffect(updateStyles, watchedProps);

  return [styles, updateStyles];
}
